import * as Sentry from "@sentry/react";
import { Navigate, Outlet, useLocation } from "@tanstack/react-router";

import { MONOLITH_SHELL_GRADIENT_CSS } from "@/components/dashboard/monolithBoardStyle";
import { useCurrentProfileQuery } from "@/lib/queries/profileQueries";
import { getSupabaseBrowserClient } from "@/lib/supabase";

import AdminBackOfficeNav from "./AdminBackOfficeNav";
import AdminLoading from "./AdminLoading";
import AdminTabs from "./AdminTabs";

const ADMIN_UI_PREVIEW_PROFILE = {
  id: "preview-superadmin",
  email: "preview@example.com",
  display_name: "Preview Superadmin",
  staff_role: "superadmin" as const,
};

export default function AdminLayout() {
  const adminUiPreview =
    import.meta.env.DEV && import.meta.env.VITE_ADMIN_UI_PREVIEW === "1";

  const { data: profile, isLoading } = useCurrentProfileQuery(
    adminUiPreview ? ADMIN_UI_PREVIEW_PROFILE : undefined,
    { enabled: !adminUiPreview },
  );

  const { pathname } = useLocation();
  const isDesignSystem = pathname.startsWith("/admin/design-system");

  if (isLoading) return <AdminLoading />;
  if (!profile) return <Navigate to="/" search={{ next: "/admin" }} />;
  if (profile.staff_role !== "superadmin") return <Navigate to="/" />;

  Sentry.setTag("staff_role", "superadmin");

  return (
    <div
      className="min-h-screen text-neutral-950 [font-family:var(--color-lab-body),Georgia,serif]"
      style={{ backgroundImage: MONOLITH_SHELL_GRADIENT_CSS }}
    >
      {isDesignSystem ? (
        <Outlet />
      ) : (
        <div className="flex min-h-screen flex-col lg:flex-row">
          <aside
            className="flex min-h-0 w-full shrink-0 flex-col border-b-[3px] border-neutral-950 bg-[#fffaf1] px-5 py-8 shadow-[8px_8px_0_rgba(19,19,19,0.1)] lg:min-h-screen lg:w-56 lg:border-b-0 lg:border-r-[3px] lg:shadow-[6px_0_0_rgba(19,19,19,0.08)]"
            aria-label="Admin"
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-neutral-600">Back office</p>
            <p className="mt-2 text-xs leading-relaxed text-neutral-700">
              Developer tools: catalog, overlays, tiles, compose, and the design system.
            </p>
            <AdminBackOfficeNav className="mt-8" />
            <div className="mt-auto flex flex-col gap-3 pt-10">
              {!adminUiPreview ? (
                <button
                  type="button"
                  onClick={async () => {
                    await getSupabaseBrowserClient().auth.signOut();
                    window.location.href = "/";
                  }}
                  className="text-left text-sm font-medium underline decoration-2 underline-offset-4 hover:text-neutral-700"
                >
                  Sign out
                </button>
              ) : (
                <span className="border-2 border-neutral-950 bg-[#fff2d9] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-950">
                  UI preview (no auth)
                </span>
              )}
            </div>
          </aside>

          <div className="min-h-0 min-w-0 flex-1">
            <header className="border-b-[3px] border-neutral-950 bg-[#fffaf1] px-6 py-4 shadow-[0_4px_0_rgba(19,19,19,0.06)]">
              <AdminTabs />
            </header>
            <Outlet />
          </div>
        </div>
      )}
    </div>
  );
}
